/* eslint-disable react-refresh/only-export-components */
import { DimeButton, DimeSeparator, DimeSpinner } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/spinner')({
  component: SpinnerDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function SpinnerDocs() {
  const [loading, setLoading] = React.useState(false);

  function handleClick() {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  }

  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Spinner</h1>
      <p className="mb-8 text-muted-foreground">
        A lightweight loading indicator built with Tailwind{' '}
        <code className="font-mono text-sm">animate-spin</code>. Zero dependencies.
      </p>

      <DocsSection title="Sizes">
        <Preview>
          <DimeSpinner className="size-3" />
          <DimeSpinner />
          <DimeSpinner className="size-5" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The default spinner is <code className="font-mono">size-4</code>. Use{' '}
          <code className="font-mono">className</code> with a{' '}
          <code className="font-mono">size-*</code> utility to scale it.
        </p>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">className</th>
              <th className="py-2 pr-4 font-medium">px</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                {
                  value: 'size-3',
                  px: '12',
                  description: 'Inline with small text or inside compact buttons.',
                },
                {
                  value: '(default)',
                  px: '16',
                  description: 'Standard — fits inside a default-size button.',
                },
                {
                  value: 'size-5',
                  px: '20',
                  description: 'Standalone loading states or large buttons.',
                },
              ] as const
            ).map((row) => (
              <tr key={row.value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.value}</td>
                <td className="py-2 pr-4 text-muted-foreground">{row.px}</td>
                <td className="py-2 text-muted-foreground">{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Button loading state">
        <Preview>
          <DimeButton onClick={handleClick} disabled={loading}>
            {loading && <DimeSpinner />}
            {loading ? 'Saving…' : 'Save changes'}
          </DimeButton>
          <DimeButton variant="outline" disabled>
            <DimeSpinner />
            Loading
          </DimeButton>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Compose <code className="font-mono">DimeSpinner</code> inside a{' '}
          <code className="font-mono">DimeButton</code> to show a loading state. Disable the button
          while the operation is in progress.
        </p>
      </DocsSection>

      <DocsSection title="Colour">
        <Preview>
          <DimeSpinner className="text-primary" />
          <DimeSpinner className="text-destructive" />
          <DimeSpinner className="text-muted-foreground" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The spinner inherits <code className="font-mono">currentColor</code>, so set any Tailwind
          text colour class via <code className="font-mono">className</code> to match context.
        </p>
      </DocsSection>
    </div>
  );
}
